import {
  deleteViolation,
  getallMemberViolations,
  resetViolation,
} from "../../../Redux/Violation/ViolationActions";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal/ConfirmationModal";
import EditViolation from "../../../Components/Admin/Violations/EditViolation/EditViolation";
import { ColDef, GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import { hasAdminPermission } from "../../../Util/adminPermissions";
import Loading from "../../../Components/Common/Loading/Loading";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import { AppDispatch, RootState } from "../../../Redux/store";
import Modal from "../../../Components/Common/Modal/Modal";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { HiDotsVertical } from "react-icons/hi";
import { IoCloseSharp } from "react-icons/io5";
import styles from "./Violations.module.scss";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";

interface ViolationType {
  memberViolationId: number;
  memberId: number;
  memberName: string;
  email: string;
  description: string;
  score: number;
  image: string | null;
  isActive: boolean;
  isDeleted: boolean;
  createdBy: number;
  createdAt: string;
  modifiedBy: number | null;
  modifiedAt: string | null;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

function Violations() {
  const dispatch = useDispatch<AppDispatch>();
  const { adminPermissions } = useSelector((state: RootState) => state.auth);
  const canManage = hasAdminPermission(
    adminPermissions,
    "memberviolations_manage",
  );
  const {
    violationsList,
    violationsListLoading,
    resetMemberViolationLoading,
    deleteMemberViolationLoading,
  } = useSelector((state: RootState) => state.violations);
  const [rowData, setRowData] = useState<ViolationType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [
    resetViolationConfirmationModalOpen,
    setResetViolationConfirmationModalOpen,
  ] = useState(false);
  const [resetViolationMemberId, setResetViolationMemberId] =
    useState<number>(-1);
  const [
    deleteViolationConfirmationModalOpen,
    setDeleteViolationConfirmationModalOpen,
  ] = useState(false);
  const [deleteViolationMemberId, setDeleteViolationMemberId] =
    useState<number>(-1);
  const [selectedViolation, setSelectedViolation] =
    useState<ViolationType | null>(null);
  const [editViolationModalOpen, setEditViolationModalOpen] = useState(false);

  const filteredRowData = rowData?.filter((item) => {
    const searchValue = search?.toLowerCase();
    if (!searchValue) return true;

    return (
      item?.memberName?.toLowerCase()?.includes(searchValue) ||
      item?.email?.toLowerCase()?.includes(searchValue) ||
      item?.description?.toLowerCase()?.includes(searchValue) ||
      String(item?.score ?? "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  });

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const confirmResetViolationHandler = () => {
    if (!resetMemberViolationLoading) {
      const successHandler = () => {
        setResetViolationConfirmationModalOpen(false);
        setResetViolationMemberId(-1);
        dispatch(getallMemberViolations());
      };
      dispatch(
        resetViolation({ memberId: resetViolationMemberId, successHandler }),
      );
    }
  };

  const confirmDeleteViolationHandler = () => {
    if (!deleteMemberViolationLoading) {
      const successHandler = () => {
        setDeleteViolationConfirmationModalOpen(false);
        setDeleteViolationMemberId(-1);
        dispatch(getallMemberViolations());
      };
      dispatch(
        deleteViolation({
          violationId: deleteViolationMemberId,
          successHandler,
        }),
      );
    }
  };

  const columnDefs: ColDef<ViolationType>[] = [
    {
      headerName: "Member",
      field: "memberName",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params?.data?.memberName || "N/A",
    },
    {
      headerName: "Email",
      field: "email",
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params?.data?.email || "N/A",
    },
    {
      headerName: "Description",
      field: "description",
      flex: 1.5,
      minWidth: 200,
      valueGetter: (params) => params?.data?.description || "N/A",
    },
    {
      headerName: "Score",
      field: "score",
      flex: 0.5,
      minWidth: 100,
    },
    {
      headerName: "Status",
      field: "isActive",
      flex: 0.5,
      minWidth: 100,
      valueGetter: (params) => (params?.data?.isActive ? "Active" : "Inactive"),
      cellClass: (params) => {
        return params?.data?.isActive ? "activeText" : "expiredText";
      },
    },
    {
      headerName: "Created At",
      field: "createdAt",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => {
        return params?.data?.createdAt
          ? moment(params.data.createdAt).format("MM/DD/YYYY")
          : "N/A";
      },
    },
    ...(canManage
      ? [
          {
            width: 20,
            colId: "actions",
            sortable: false,
            suppressAutoSize: true,
            suppressMovable: true,
            suppressNavigable: true,
            editable: false,
            cellStyle: {
              pointerEvents: "all",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            },
            cellRenderer: (params: ICellRendererParams<ViolationType>) => {
              return (
                <div className={styles.iconContainer}>
                  <Menu
                    direction="left"
                    menuButton={
                      <MenuButton>
                        <HiDotsVertical className={styles.icon} />
                      </MenuButton>
                    }
                    transition
                    portal
                  >
                    <MenuItem
                      onClick={() => {
                        setSelectedViolation(params?.data || null);
                        setEditViolationModalOpen(true);
                      }}
                    >
                      Edit
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setDeleteViolationConfirmationModalOpen(true);
                        setDeleteViolationMemberId(
                          params?.data?.memberViolationId || -1,
                        );
                      }}
                    >
                      Delete
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setResetViolationConfirmationModalOpen(true);
                        setResetViolationMemberId(params?.data?.memberId || -1);
                      }}
                    >
                      Reset All Violations
                    </MenuItem>
                  </Menu>
                </div>
              );
            },
          } as ColDef<ViolationType>,
        ]
      : []),
  ];

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getallMemberViolations());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (violationsList) {
      setRowData(violationsList);
    }
  }, [violationsList]);

  return (
    <React.Fragment>
      {violationsListLoading && <Loading />}
      <div className={styles.mainContainer}>
        <div className={styles.topContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Violations</h1>
          </div>
          <div className={styles.buttonContainer}></div>
        </div>
        <div className={styles.bodyContainer}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              className={styles.input}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e?.target?.value || "")}
            />
            <div className={styles.iconContainer} onClick={() => setSearch("")}>
              <IoCloseSharp className={styles.icon} />
            </div>
          </div>

          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50, 100]}
              animateRows={true}
              domLayout="autoHeight"
            />
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={resetViolationConfirmationModalOpen}
        setIsOpen={setResetViolationConfirmationModalOpen}
        onConfirm={confirmResetViolationHandler}
        heading="Reset Violation"
        text="Are you sure you want to reset all violations for this member?"
        loading={resetMemberViolationLoading}
      />

      <ConfirmationModal
        isOpen={deleteViolationConfirmationModalOpen}
        setIsOpen={setDeleteViolationConfirmationModalOpen}
        onConfirm={confirmDeleteViolationHandler}
        heading="Delete Violation"
        text="Are you sure you want to delete this violation?"
        loading={deleteMemberViolationLoading}
      />

      <Modal
        isOpen={editViolationModalOpen}
        setIsOpen={setEditViolationModalOpen}
      >
        <EditViolation
          isOpen={editViolationModalOpen}
          setIsOpen={setEditViolationModalOpen}
          violationData={selectedViolation}
          updateSuccessHandler={() => {
            dispatch(getallMemberViolations());
          }}
        />
      </Modal>
    </React.Fragment>
  );
}

export default Violations;
